/**
 * Localization Formatting Utilities
 * 
 * Provides locale-aware formatting for numbers, decimals, and file sizes
 * using Intl.NumberFormat for proper international number display
 */

import { LanguageContext } from '@axon-enterprise/i18n-helpers';
import { useCallback, useContext, useMemo } from 'react';

/**
 * Hook to get locale-aware number formatters
 * 
 * @returns Object with formatNumber and formatDecimal functions
 * 
 * @example
 * const { formatNumber, formatDecimal } = useNumberFormatter();
 * formatNumber(1000) // US: "1,000" | FR: "1 000"
 * formatDecimal(1.5, 2) // US: "1.50" | FR: "1,50"
 */
export function useNumberFormatter() {
  const { language } = useContext(LanguageContext);
  
  /**
   * Format integers with locale-aware thousand separators
   * @param num - The number to format
   * @returns Formatted number string
   */
  const formatNumber = useCallback((num: number): string => {
    return new Intl.NumberFormat(language).format(num);
  }, [language]);
  
  /**
   * Format decimals with locale-aware decimal separator
   * @param num - The number to format
   * @param decimals - Number of decimal places
   * @returns Formatted decimal string
   */
  const formatDecimal = useCallback((num: number, decimals: number): string => {
    return new Intl.NumberFormat(language, { 
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals 
    }).format(num);
  }, [language]);
  
  return useMemo(() => ({ formatNumber, formatDecimal }), [formatNumber, formatDecimal]);
}

/**
 * Hook to get locale-aware file size formatter
 * 
 * @returns Function to format file sizes in bytes to human-readable format
 * 
 * @example
 * const formatFileSize = useFileSizeFormatter();
 * formatFileSize(1536) // US: "1.50 KB" | FR: "1,50 KB"
 */
export function useFileSizeFormatter() {
  const { formatDecimal } = useNumberFormatter();
  
  return useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    // For bytes (< 1KB), don't show decimals
    if (i === 0) {
      return `${bytes} ${sizes[i]}`;
    }
    
    return `${formatDecimal(bytes / Math.pow(k, i), 2)} ${sizes[i]}`;
  }, [formatDecimal]);
}

